import React, { useEffect, useState } from 'react';
import { countdownService } from '../../services/api';
import type { SiteCountdownData, SiteCountdownUpdateRequest } from '../../types';

function toLocalInputValue(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const AdminHomeCountdown: React.FC = () => {
    const [form, setForm] = useState<SiteCountdownUpdateRequest>({
        labelPtBr: 'Grande Abertura',
        labelEn: 'Grand Opening',
        targetDateUtc: new Date().toISOString(),
        isEnabled: true,
        textColor: '#8a6d1f',
        labelFontSizePx: 10,
        valueFontSizePx: 20,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const data: SiteCountdownData = await countdownService.getCountdownAdmin();
                setForm({
                    labelPtBr: data.labelPtBr,
                    labelEn: data.labelEn,
                    targetDateUtc: data.targetDateUtc,
                    isEnabled: data.isEnabled,
                    textColor: data.textColor || '#8a6d1f',
                    labelFontSizePx: data.labelFontSizePx ?? 10,
                    valueFontSizePx: data.valueFontSizePx ?? 20,
                });
            } catch {
                setMessage('Erro ao carregar countdown.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        try {
            await countdownService.updateCountdown(form);
            setMessage('Countdown salvo com sucesso.');
        } catch {
            setMessage('Erro ao salvar countdown.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p>Carregando countdown...</p>;

    return (
        <div className="admin-form-wrapper" style={{ marginBottom: 24 }}>
            <h2 style={{ marginBottom: 16, fontSize: 18 }}>Countdown (banco de dados)</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: 14 }}>
                A data fica na tabela <code>SiteCountdown</code> do banco WEBSITE e aparece no hero, ao lado de Criar Conta.
            </p>
            <div className="form-group">
                <label>
                    <input
                        type="checkbox"
                        checked={form.isEnabled}
                        onChange={e => setForm(f => ({ ...f, isEnabled: e.target.checked }))}
                    />
                    {' '}Exibir countdown na home
                </label>
            </div>
            <div className="form-group">
                <label>Rótulo (PT-BR)</label>
                <input
                    type="text"
                    value={form.labelPtBr || ''}
                    onChange={e => setForm(f => ({ ...f, labelPtBr: e.target.value }))}
                />
            </div>
            <div className="form-group">
                <label>Rótulo (EN)</label>
                <input
                    type="text"
                    value={form.labelEn || ''}
                    onChange={e => setForm(f => ({ ...f, labelEn: e.target.value }))}
                />
            </div>
            <div className="form-group">
                <label>Data e hora alvo</label>
                <input
                    type="datetime-local"
                    value={toLocalInputValue(form.targetDateUtc)}
                    onChange={e => {
                        const v = e.target.value;
                        setForm(f => ({
                            ...f,
                            targetDateUtc: v ? new Date(v).toISOString() : f.targetDateUtc,
                        }));
                    }}
                />
            </div>
            <div className="form-row-inline">
                <div className="form-group">
                    <label>Cor do texto</label>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <input
                            type="color"
                            value={form.textColor || '#8a6d1f'}
                            onChange={e => setForm(f => ({ ...f, textColor: e.target.value }))}
                        />
                        <input
                            type="text"
                            value={form.textColor || '#8a6d1f'}
                            onChange={e => setForm(f => ({ ...f, textColor: e.target.value }))}
                            placeholder="#8a6d1f"
                        />
                    </div>
                </div>
            </div>
            <div className="form-row-inline">
                <div className="form-group">
                    <label>Tamanho do rótulo (px)</label>
                    <input
                        type="number"
                        min={8}
                        max={32}
                        value={form.labelFontSizePx ?? 10}
                        onChange={e => setForm(f => ({ ...f, labelFontSizePx: Number(e.target.value) }))}
                    />
                </div>
                <div className="form-group">
                    <label>Tamanho dos números (px)</label>
                    <input
                        type="number"
                        min={12}
                        max={48}
                        value={form.valueFontSizePx ?? 20}
                        onChange={e => setForm(f => ({ ...f, valueFontSizePx: Number(e.target.value) }))}
                    />
                </div>
            </div>
            {message && (
                <p style={{ marginBottom: 12, color: message.includes('sucesso') ? '#28a745' : '#dc3545' }}>
                    {message}
                </p>
            )}
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar countdown'}
            </button>
        </div>
    );
};

export default AdminHomeCountdown;
